// @<COPYRIGHT>@
// ==================================================
// Copyright (c) 2020 Siemens
// Siemens Product Lifecycle Management Software Inc.
// All Rights Reserved.
// ==================================================
// @<COPYRIGHT>@

/**
 * This module provides workspace table actions.
 *
 * @module js/swf3dWorkspaceBreadcrumbService
 */

// import from Apollo: the module from Apollo's library
import AwNavigateBreadcrumb from 'viewmodel/AwNavigateBreadcrumbViewModel';
import appCtxSvc from 'js/appCtxService';
import eventBus from 'js/eventBus';
import config from 'js/swf3dWorkspaceConfig';

let localBreadcrumbs = [];

/**
 *
 * @param selectedCrumb
 */
function onCrumbClickFn(selectedCrumb) {
    if (selectedCrumb.type === 'Home' && config.storage === 'collab') {
        config.resourceType = 'projects';
        config.projectId = '';
        appCtxSvc.updatePartialCtx('galaxy.workspace.tableCurrentFolderUrn', '');
    } else {
        appCtxSvc.updatePartialCtx('galaxy.workspace.tableCurrentFolderUrn', selectedCrumb.urn);
    }
    // Clear search text and reset table when click breadcrumb, as the search result may not be correct after user click breadcrumb to switch folder.
    appCtxSvc.updatePartialCtx('galaxy.workspace.table.searchText', '');
    eventBus.publish('galaxy.workspace.resetTable');
    eventBus.publish('galaxy.workspace.updateBreadcrumb', { level: selectedCrumb.level });
}

/**
 * Default render function for breadcrumb
 *
 * @memberof js/swf3dWorkspaceBreadcrumbService
 * @function render
 * @param viewModel.viewModel
 * @param {object} viewModel
 */
export const render = ({ viewModel }) => {
    let { data } = viewModel;
    if (localBreadcrumbs.length > 0) {
        data.dynamicCrumbs = localBreadcrumbs;
    }
    data.dynamicCrumbs[0].onCrumbClick = onCrumbClickFn;
    return <AwNavigateBreadcrumb id='swf3d-workspace-bread-crumb' crumbs={data.dynamicCrumbs}></AwNavigateBreadcrumb>;
};

/**
 * Function for build breadcrumb
 *
 * @memberof js/swf3dWorkspaceBreadcrumbService
 * @function buildBreadcrumb
 * @param {object} eventData
 * @param {object} originalCrumbs
 * @example
 * swf3dWorkspaceBreadcrumbService.buildBreadcrumb(eventData, originalCrumbs);
 * @returns {object} - crumbs data
 */
export let buildBreadcrumb = (eventData, originalCrumbs) => {
    let dynamicCrumbs = [...originalCrumbs];

    const newCrumb = {
        clicked: true,
        displayName: eventData.displayName,
        selectedCrumb: false,
        showArrow: false,
        type: 'Folder',
        urn: eventData.urn,
        level: dynamicCrumbs.length + 1,
        scopedUid: dynamicCrumbs.length + 1,
        onCrumbClick: onCrumbClickFn
    };

    dynamicCrumbs[dynamicCrumbs.length - 1].showArrow = true;
    dynamicCrumbs.push(newCrumb);
    localBreadcrumbs = [...dynamicCrumbs];
    return { crumbs: dynamicCrumbs };
};

/**
 * Function for update breadcrumb
 *
 * @memberof js/swf3dWorkspaceBreadcrumbService
 * @function updateBreadcrumb
 * @param {object} eventData
 * @param {object} originalCrumbs
 * @example
 * swf3dWorkspaceBreadcrumbService.updateBreadcrumb(eventData, originalCrumbs);
 * @returns {object} - crumbs data
 */
export let updateBreadcrumb = (eventData, originalCrumbs) => {
    let dynamicCrumbs = [...originalCrumbs];
    while (eventData.level < dynamicCrumbs.length) {
        dynamicCrumbs.pop();
    }
    localBreadcrumbs = [...dynamicCrumbs];
    return { crumbs: dynamicCrumbs };
};
